import React, { useEffect, useRef, useState } from "react";
import useUserSearch from "../../hooks/useUserSearch";
import api from "../../api/api";

export default function ShareDocModal({ doc, onClose }) {
    const { search, results, loading, clearResults } = useUserSearch();
    const [query, setQuery] = useState("");
    const [shares, setShares] = useState([]);
    const [sharesLoading, setSharesLoading] = useState(true);
    const [sharing, setSharing] = useState(false);
    const [error, setError] = useState("");
    const overlayRef = useRef(null);

    // Load existing shares on open
    useEffect(() => {
        setSharesLoading(true);
        api.listDocShares(doc.id)
            .then((res) => setShares(res.data ?? []))
            .catch(() => {})
            .finally(() => setSharesLoading(false));
    }, [doc.id]);

    // Close on overlay click
    useEffect(() => {
        const handler = (e) => { if (e.target === overlayRef.current) onClose(); };
        document.addEventListener("pointerdown", handler);
        return () => document.removeEventListener("pointerdown", handler);
    }, [onClose]);

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setError("");
        if (val.trim().length >= 1) {
            search(val);
        } else {
            clearResults();
        }
    };

    const handleShare = async (user) => {
        if (sharing) return;
        const alreadyShared = shares.some((s) => String(s.shared_with) === String(user.id));
        if (alreadyShared) { setError("Already shared with this user."); return; }

        setSharing(true);
        setError("");
        try {
            const res = await api.shareDocPage(doc.id, String(user.id));
            setShares((prev) => [...prev, res.data]);
            setQuery("");
            clearResults();
        } catch {
            setError("Failed to share. Try again.");
        } finally {
            setSharing(false);
        }
    };

    const handleRevoke = async (sharedWith) => {
        try {
            await api.revokeDocShare(doc.id, sharedWith);
            setShares((prev) => prev.filter((s) => s.shared_with !== sharedWith));
        } catch {
            setError("Failed to revoke.");
        }
    };

    return (
        <div
            ref={overlayRef}
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center",
            }}
        >
            <div style={{
                background: "#fff", borderRadius: 12, padding: "24px 24px 20px",
                width: 420, maxWidth: "92vw",
                boxShadow: "0 8px 40px rgba(0,0,0,0.22)",
                display: "flex", flexDirection: "column", gap: 14,
            }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Share document</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>"{doc.title}"</div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af", lineHeight: 1 }}
                    >×</button>
                </div>

                {/* Search */}
                <div style={{ position: "relative" }}>
                    <input
                        autoFocus
                        value={query}
                        onChange={handleQueryChange}
                        placeholder="Search by name or username…"
                        style={{
                            width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                            border: "1px solid #d1d5db", outline: "none", boxSizing: "border-box",
                        }}
                        onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; }}
                        onBlur={(e) => { e.target.style.borderColor = "#d1d5db"; }}
                    />

                    {/* Dropdown results */}
                    {(loading || results.length > 0) && (
                        <div style={{
                            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.10)", maxHeight: 180, overflowY: "auto",
                            marginTop: 2,
                        }}>
                            {loading && (
                                <div style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>
                                    Searching…
                                </div>
                            )}
                            {results.map((user) => (
                                <div
                                    key={user.id}
                                    onMouseDown={(e) => { e.preventDefault(); handleShare(user); }}
                                    style={{
                                        padding: "9px 12px", cursor: sharing ? "not-allowed" : "pointer",
                                        fontSize: 13, display: "flex", justifyContent: "space-between",
                                        alignItems: "center", borderBottom: "1px solid #f3f4f6",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f0f9ff"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                                >
                                    <span style={{ fontWeight: 500, color: "#111827" }}>{user.name}</span>
                                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{user.user_type_name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {error && (
                    <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>
                )}

                {/* Current shares */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>
                        {sharesLoading ? "Loading…" : shares.length === 0 ? "Not shared with anyone" : "Shared with"}
                    </div>
                    {shares.map((s) => (
                        <div key={s.shared_with} style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{
                                    width: 28, height: 28, borderRadius: "50%",
                                    background: "#e0f2fe", color: "#0369a1",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                                }}>
                                    {(s.shared_with || "?")[0].toUpperCase()}
                                </div>
                                <span style={{ color: "#374151" }}>User {s.shared_with}</span>
                                <span style={{
                                    fontSize: 10, padding: "1px 6px", borderRadius: 10,
                                    background: "#dcfce7", color: "#166534", fontWeight: 600,
                                }}>
                                    {s.permission}
                                </span>
                            </div>
                            <button
                                onClick={() => handleRevoke(s.shared_with)}
                                style={{
                                    border: "none", background: "none", color: "#ef4444",
                                    cursor: "pointer", fontSize: 12, fontWeight: 600,
                                }}
                            >
                                Revoke
                            </button>
                        </div>
                    ))}
                </div>

                <div style={{ fontSize: 11, color: "#9ca3af", borderTop: "1px solid #f3f4f6", paddingTop: 10 }}>
                    Shared users can edit this document. Last save wins.
                </div>
            </div>
        </div>
    );
}
